import React, { useEffect, useState } from 'react'
import { Popover } from 'react-tiny-popover'
import { useDispatch } from 'react-redux'
import _ from 'lodash'

import InputFilter from './InputFilter'
import ListFilterTab from './ListFilterTab'
import PopoverWrapper from './PopoverWrapper'
import Range from './Range'

import {filterKeys, filters, moreFilterOptions} from '../../../configuration/filtering'
import labels from '../../../configuration/labels'
import { useGetMoreFilterContentFromResults } from '../../../hooks/useGetMoreFilterContentFromResults'
import { useGetMaxFilterOptionForMoreFiltersType } from '../../../hooks/useGetMaxFilterOptionForMoreFiltersType'
import { useGetPickedFilterSetsByKeys } from '../../../hooks/useGetPickedFilterSetsByKeys'
import { updateMultipleFilters } from '../../../redux/actions/applyFilters'
import { onEnter } from '../../../utilities/accessibility/onEnter'
import { getHasMoreFiltersSelections } from '../../../utilities/filters/validators'

import '../../../styles/scss/filters/popover-more-filters.scss'

const INITIAL_MIN_DESTINATIONS = 4
const INITIAL_MAX_DESTINATIONS = 8
const INITIAL_DESTINATIONS_RANGE = 15

const PopoverMoreFilters = () => {
  const dispatch = useDispatch()
  const activeFilters = useGetPickedFilterSetsByKeys(moreFilterOptions)
  const [currentFilters, setCurrentFilters] = useState(activeFilters)
  const [popoverOpen, setPopoverOpen] = useState(false)

  const destinationEndFilterOptions = useGetMoreFilterContentFromResults(filterKeys.destinationEnd)
  const destinationStartFilterOptions = useGetMoreFilterContentFromResults(filterKeys.destinationStart)
  const famousRoutesFilterOptions = useGetMoreFilterContentFromResults(filterKeys.famousRoutes)

  const minDestinations = _.get(currentFilters, `[${filterKeys.destinationCount}].min`) !== ''
    ? _.get(currentFilters, `[${filterKeys.destinationCount}].min`)
    : INITIAL_MIN_DESTINATIONS
  const maxDestinations = _.get(currentFilters, `[${filterKeys.destinationCount}].max`) !== ''
    ? _.get(currentFilters, `[${filterKeys.destinationCount}].max`)
    : INITIAL_MAX_DESTINATIONS
  const maxDestinationsRange = useGetMaxFilterOptionForMoreFiltersType(filterKeys.destinationCount)
  const destinationsRange = maxDestinationsRange || INITIAL_DESTINATIONS_RANGE

  useEffect(() => {
    let updatedMin = minDestinations
    let updatedMax = maxDestinations
    if (destinationsRange < minDestinations || destinationsRange < maxDestinations) {
      updatedMin = destinationsRange < minDestinations
        ? 1
        : minDestinations
      updatedMax = destinationsRange < maxDestinations
        ? destinationsRange
        : maxDestinations
      setCurrentFilters({
        ...currentFilters,
        [filterKeys.destinationCount]: {
          min: updatedMin,
          max: updatedMax,
        }
      })
    }
  }, [currentFilters, destinationsRange, minDestinations, maxDestinations])

  useEffect(() => {
    setCurrentFilters(activeFilters)
  }, [activeFilters, popoverOpen])

  function onClearAllFilters() {
    setPopoverOpen(false)
    const moreFilters = _.pick(filters, moreFilterOptions)
    const clearedFilters = _.reduce(moreFilters, (filterMap, filter, filterKey) => ({
      ...filterMap,
      [`${filterKey}`]: filter.initialState,
    }), {})
    dispatch(updateMultipleFilters(clearedFilters))
  }

  function onOpenPopover() {
    setPopoverOpen(true)
  }

  function onSubmitFilters() {
    setPopoverOpen(false)
    dispatch(updateMultipleFilters(currentFilters))
  }

  function onRemoveDestinationFilter(key, filter) {
    const updatedFilters = {
      ...currentFilters,
      [`${key}`]: _.without(
        _.get(currentFilters, `[${key}]`),
        filter,
      )
    }
    setCurrentFilters(updatedFilters)
  }

  function onAddDestinationFilter(key, value) {
    const filterOptions = key === filterKeys.destinationStart
      ? destinationStartFilterOptions
      : destinationEndFilterOptions
    const destinationFilterKey = _.findKey(filterOptions, filter => filter === value)
    if (destinationFilterKey) {
      const updatedFilters = {
        ...currentFilters,
        [`${key}`]: _.concat(
          _.get(currentFilters, `[${key}]`),
          destinationFilterKey,
        )
      }
      setCurrentFilters(updatedFilters)
    }
  }

  function onModifyListFilter(key, value) {
    const currentListFiltersForKey =  _.get(currentFilters, `[${key}]`)
    const modify = _.includes(currentListFiltersForKey, value)
      ? _.without
      : _.concat;
    const updatedFilters = {
      ...currentFilters,
      [`${key}`]: modify(currentListFiltersForKey, value)
    }
    setCurrentFilters(updatedFilters)
  }

  function onModifySliderRange(key, field, value) {
    const updatedFilters = {
      ...currentFilters,
      [key]: {
        ..._.get(currentFilters, `[${key}]`),
        [`${field}`]: value
      }
    }
    setCurrentFilters(updatedFilters)
  }

  const hasActiveFilters = getHasMoreFiltersSelections(activeFilters)
  const hasAvailableFilterTypeOptions = !_.isEmpty(destinationEndFilterOptions)
    || !_.isEmpty(destinationStartFilterOptions)
    || !_.isEmpty(famousRoutesFilterOptions)

  return hasAvailableFilterTypeOptions && (
    <div className="popover-more-filters">
      <Popover
        containerClassName="popover-more-filters-modal"
        containerParent={document.getElementById('ylg-ms__site-search-listings')}
        containerStyle={{boxShadow: '2px 1px 4px rgba(0, 0, 0, 0.25)', overflowY: 'scroll'}}
        isOpen={popoverOpen}
        onClickOutside={event => {
          const targetId = _.get(event, 'target.id')
          if (!_.includes(['autocomplete-item', 'list-filter-tab'], targetId)) {
            setPopoverOpen(false)
          }
        }}
        positions={['top', 'bottom', 'left', 'right']}
        content={(
          <PopoverWrapper
            onClear={onClearAllFilters}
            onClose={() => setPopoverOpen(false)}
            onSubmit={onSubmitFilters}
            submitLabel={labels.moreFiltersSubmit}
            title={labels.moreFilters}
          >
            <div className="popover-more-filters-modal__body">
              {_.includes(moreFilterOptions, filterKeys.destinationStart) &&
                !_.isEmpty(destinationStartFilterOptions) && (
                  <div>
                    <span className="popover-more-filters-modal__body__title">
                      {_.get(filters, `[${filterKeys.destinationStart}].label`)}
                    </span>
                    <div className="popover-more-filters-modal__body__spacer">
                      <InputFilter
                        availableFilters={destinationStartFilterOptions}
                        currentFilters={_.get(currentFilters, `[${filterKeys.destinationStart}]`)}
                        label={_.get(filters, `[${filterKeys.destinationStart}].label`)}
                        onRemoveFilter={filter => onRemoveDestinationFilter(filterKeys.destinationStart, filter)}
                        onSubmitInput={filter => onAddDestinationFilter(filterKeys.destinationStart, filter)}
                        placeholder={_.get(filters, `[${filterKeys.destinationStart}].placeholder`)}
                      />
                    </div>
                  </div>
                )}
              {_.includes(moreFilterOptions, filterKeys.destinationEnd) &&
                !_.isEmpty(destinationEndFilterOptions) && (
                  <div>
                    <span className="popover-more-filters-modal__body__title">
                      {_.get(filters, `[${filterKeys.destinationEnd}].label`)}
                    </span>
                    <div className="popover-more-filters-modal__body__spacer">
                      <InputFilter
                        availableFilters={destinationEndFilterOptions}
                        currentFilters={_.get(currentFilters, `[${filterKeys.destinationEnd}]`)}
                        label={_.get(filters, `[${filterKeys.destinationEnd}].label`)}
                        onRemoveFilter={filter => onRemoveDestinationFilter(filterKeys.destinationEnd, filter)}
                        onSubmitInput={filter => onAddDestinationFilter(filterKeys.destinationEnd, filter)}
                        placeholder={_.get(filters, `[${filterKeys.destinationEnd}].placeholder`)}
                      />
                    </div>
                  </div>
                )}
              {_.includes(moreFilterOptions, filterKeys.famousRoutes) &&
                !_.isEmpty(famousRoutesFilterOptions) && (
                  <div>
                    <span className="popover-more-filters-modal__body__title">
                      {_.get(filters, `[${filterKeys.famousRoutes}].label`)}
                    </span>
                    <div className="popover-more-filters-modal__body__spacer list-tab-container">
                      {_.map(famousRoutesFilterOptions, filterOption => (
                        <ListFilterTab
                          key={filterOption}
                          isActive={_.includes(_.get(currentFilters, `[${filterKeys.famousRoutes}]`), filterOption)}
                          label={filterOption}
                          onClick={filter => onModifyListFilter(filterKeys.famousRoutes, filter)}
                          value={filterOption}
                        />
                      ))}
                    </div>
                  </div>
                )}
              {_.includes(moreFilterOptions, filterKeys.destinationCount) && (
                  <div>
                    <span className="popover-more-filters-modal__body__title">
                      {_.get(filters, `[${filterKeys.destinationCount}].label`)}
                    </span>
                    <div className="popover-more-filters-modal__body__spacer">
                      <Range
                        min={minDestinations}
                        max={maxDestinations}
                        range={destinationsRange}
                        setMin={(v) => +v >= 0 && +v < maxDestinations && onModifySliderRange(filterKeys.destinationCount, 'min', Number(v))}
                        setMax={(v) => +v >= 0 && +v <= destinationsRange && onModifySliderRange(filterKeys.destinationCount, 'max', Number(v))}
                        setMinSelect={val => onModifySliderRange(filterKeys.destinationCount, 'min', val)}
                        setMaxSelect={val => onModifySliderRange(filterKeys.destinationCount, 'max', val)}
                      />
                    </div>
                  </div>
                )}
            </div>
          </PopoverWrapper>
        )}
      >
        <div
          id="more-filters-popover-tab"
          className={`u-flex u-flex-align-center popover-more-filters__button ${hasActiveFilters ? 'active' : ''}`}
          onClick={onOpenPopover}
          onKeyDown={event => onEnter(event, onOpenPopover)}
          role="button"
          tabIndex="0"
        >
          <span>
            {labels.moreFilters}
          </span>
        </div>
      </Popover>
    </div>
  )
}

export default PopoverMoreFilters
